import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, Download, Rocket, RotateCcw, Star } from "lucide-react";

const playSound = (type) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = window.__lwbAudio || (window.__lwbAudio = new Ctx());
    if (ctx.state === "suspended") ctx.resume();
    const notes = {
      pop: [[520, 0, 0.12, "triangle"]],
      boop: [[196, 0, 0.18, "sawtooth"], [155, 0.16, 0.22, "sawtooth"]],
      fanfare: [[523, 0, 0.16, "triangle"], [659, 0.16, 0.16, "triangle"], [784, 0.32, 0.16, "triangle"], [1047, 0.48, 0.4, "triangle"]],
    }[type];
    notes.forEach(([f, t, d, wave]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = wave;
      o.frequency.value = f;
      g.gain.setValueAtTime(0.001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + d);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + t);
      o.stop(ctx.currentTime + t + d + 0.05);
    });
  } catch {}
};

const playVoice = (key, src) => {
  try {
    const store = window.__lwbVoices || (window.__lwbVoices = {});
    const a = store[key] || (store[key] = new Audio(src));
    a.currentTime = 0;
    a.volume = 0.4;
    a.play().catch(() => {});
  } catch {}
};

const IMG = {
  football: "/images/wcyd-football.png",
  dressed: "/images/wcyd-dressed.png",
  tidy: "/images/wcyd-tidy.png",
  paint: "/images/wcyd-paint.png",
  teeth: "/images/wcyd-teeth.png",
};

const CHALLENGES = [
  {
    intro: "Bailey can do so many things…",
    question: "What can Bailey do with a ball?",
    correct: "football",
    cheerImg: "football",
    cheer: "I can play football!",
    options: [
      { id: "football", name: "Play football", img: IMG.football },
      { id: "paint", name: "Paint a picture", img: IMG.paint },
      { id: "teeth", name: "Brush my teeth", img: IMG.teeth },
    ],
  },
  {
    intro: "All by himself…",
    question: "Bailey can get dressed — which picture shows that?",
    correct: "dressed",
    cheerImg: "dressed",
    cheer: "I can get dressed!",
    options: [
      { id: "tidy", name: "Tidy up toys", img: IMG.tidy },
      { id: "football", name: "Play football", img: IMG.football },
      { id: "dressed", name: "Get dressed", img: IMG.dressed },
    ],
  },
  {
    intro: "Uh oh, toys everywhere…",
    question: "Which picture shows Bailey tidying up?",
    correct: "tidy",
    cheerImg: "tidy",
    cheer: "I can tidy up!",
    options: [
      { id: "teeth", name: "Brush my teeth", img: IMG.teeth },
      { id: "tidy", name: "Tidy up toys", img: IMG.tidy },
      { id: "paint", name: "Paint a picture", img: IMG.paint },
    ],
  },
  {
    intro: "Something colourful to finish…",
    question: "Bailey loves to paint! Which picture shows painting?",
    correct: "paint",
    cheerImg: "paint",
    cheer: "I can paint!",
    options: [
      { id: "football", name: "Play football", img: IMG.football },
      { id: "dressed", name: "Get dressed", img: IMG.dressed },
      { id: "paint", name: "Paint a picture", img: IMG.paint },
    ],
  },
];

const BURST = [
  { x: -140, y: -90, c: "#E8564A" }, { x: 140, y: -100, c: "#FFE9A8" }, { x: -180, y: 20, c: "#4FA3E3" },
  { x: 180, y: 30, c: "#71BE6B" }, { x: -80, y: -160, c: "#F5B8A6" }, { x: 90, y: -170, c: "#E8564A" },
  { x: -60, y: 120, c: "#4FA3E3" }, { x: 70, y: 130, c: "#FFE9A8" }, { x: 0, y: -190, c: "#71BE6B" },
  { x: -190, y: -40, c: "#FFE9A8" }, { x: 190, y: -50, c: "#4FA3E3" }, { x: 0, y: 170, c: "#F5B8A6" },
];

const FIREWORKS = [
  { x: "10%", y: "16%", c: "#E8564A", delay: 0 },
  { x: "88%", y: "12%", c: "#4FA3E3", delay: 0.5 },
  { x: "76%", y: "42%", c: "#F5A623", delay: 1.0 },
  { x: "18%", y: "52%", c: "#71BE6B", delay: 1.4 },
  { x: "50%", y: "8%", c: "#E8564A", delay: 0.8 },
];

const FireworkBurst = ({ x, y, c, delay }) => (
  <div className="firework" style={{ left: x, top: y }}>
    <span className="firework-flash" style={{ animationDelay: `${delay}s` }} />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i / 12) * Math.PI * 2;
      const d = 70 + (i % 3) * 26;
      return (
        <span
          key={i}
          className="firework-dot"
          style={{
            background: i % 4 === 3 ? "#FFFDF2" : c,
            animationDelay: `${delay}s`,
            "--dx": `${Math.cos(a) * d}px`,
            "--dy": `${Math.sin(a) * d}px`,
          }}
        />
      );
    })}
  </div>
);

const Streamer = ({ color, style, delay = 0 }) => (
  <svg className="streamer" style={{ ...style, animationDelay: `${delay}s` }} width="26" height="120" viewBox="0 0 26 120">
    <path d="M13 0 C 24 14 2 24 13 38 C 24 52 2 62 13 76 C 24 90 2 100 13 116" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" />
  </svg>
);

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

export default function WhatCanYouDoGame() {
  const [stage, setStage] = useState("welcome");
  const [round, setRound] = useState(0);
  const [cards, setCards] = useState(() => shuffle(CHALLENGES[0].options));
  const [shakeId, setShakeId] = useState(null);
  const [oops, setOops] = useState(false);
  const [best, setBest] = useState(() => Number(localStorage.getItem("lwb-wcyd-stars") || 0));

  const ch = CHALLENGES[round];
  const stars = stage === "complete" ? CHALLENGES.length : stage === "cheer" ? round + 1 : round;

  const start = () => {
    playSound("pop");
    setRound(0);
    setCards(shuffle(CHALLENGES[0].options));
    setStage("challenge");
  };

  const pick = (c) => {
    if (c.id === ch.correct) {
      playSound("fanfare");
      playVoice("found", "/audio/you-found-it.mp3");
      const n = Math.max(best, round + 1);
      localStorage.setItem("lwb-wcyd-stars", String(n));
      setBest(n);
      setStage("cheer");
    } else {
      playSound("boop");
      setOops(true);
      setShakeId(c.id + Date.now());
      setTimeout(() => setOops(false), 2400);
    }
  };

  const next = () => {
    playSound("pop");
    if (round < CHALLENGES.length - 1) {
      const r = round + 1;
      setRound(r);
      setCards(shuffle(CHALLENGES[r].options));
      setStage("challenge");
    } else {
      playVoice("yay", "/audio/yay.mp3");
      setStage("complete");
    }
  };

  return (
    <div
      data-testid="wcyd-game"
      className="min-h-screen relative overflow-hidden flex flex-col"
      style={{ background: "linear-gradient(180deg, #FDEBE0 0%, #FEF4EC 55%, #FFFDF2 100%)" }}
    >
      <svg className="cloud cloud-a" viewBox="0 0 80 40" width="90" height="45">
        <ellipse cx="28" cy="28" rx="22" ry="11" fill="#FFFFFF" />
        <ellipse cx="48" cy="22" rx="18" ry="12" fill="#FFFFFF" />
      </svg>
      <svg className="cloud cloud-b" viewBox="0 0 80 40" width="64" height="32">
        <ellipse cx="26" cy="28" rx="20" ry="10" fill="#FFFFFF" />
        <ellipse cx="44" cy="22" rx="16" ry="11" fill="#FFFFFF" />
      </svg>

      <header className="relative z-20 flex items-center justify-between px-5 sm:px-8 py-4">
        <Link to="/" data-testid="wcyd-home-link" className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
          <img src="/images/lwb-logo.png" alt="Learn With Bailey" className="h-9 w-auto" />
        </Link>
        {best > 0 && (
          <span data-testid="wcyd-stars-chip" className="rounded-full bg-amberbrand text-cream text-xs font-bold uppercase tracking-widest px-4 py-2 shadow inline-flex items-center gap-1.5">
            <Star size={13} fill="currentColor" /> Stars {best}/4
          </span>
        )}
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 pb-16">
        <AnimatePresence mode="wait">
          {stage === "welcome" && (
            <motion.div
              key="welcome"
              data-testid="wcyd-welcome"
              className="text-center max-w-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -30 }}
            >
              <motion.img
                src={IMG.football}
                alt="Bailey kicking a football"
                data-testid="wcyd-hero-character"
                className="w-40 sm:w-52 mx-auto mb-2 drop-shadow-[0_18px_24px_rgba(49,67,92,0.35)]"
                initial={{ y: 120, opacity: 0, scale: 0.6 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 120, damping: 11, delay: 0.2 }}
              />
              <motion.h1
                className="font-display font-extrabold tracking-tight text-3xl sm:text-5xl lg:text-6xl text-walnut"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.45, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                Bailey, What Can You Do?
              </motion.h1>
              <motion.div
                className="mt-5 bg-white/85 border-2 border-sand rounded-3xl px-7 py-5 shadow-lg inline-block text-left"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="font-display font-bold text-xl sm:text-2xl text-walnut">I can do LOTS of things!</p>
                <p className="text-base sm:text-lg text-walnutsoft mt-1">Football, getting dressed, tidying up, painting…</p>
                <p className="text-base sm:text-lg text-walnutsoft">Can you find them all?</p>
              </motion.div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 160, damping: 10, delay: 1.05 }}
              >
                <motion.button
                  data-testid="wcyd-start-button"
                  onClick={start}
                  className="mt-8 rounded-full text-cream font-display font-extrabold text-xl sm:text-2xl px-12 py-5 inline-flex items-center gap-3 shadow-[0_20px_40px_-12px_rgba(232,86,74,0.7)]"
                  style={{ background: "#E8564A" }}
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                  whileHover={{ scale: 1.12 }}
                  whileTap={{ scale: 0.94 }}
                >
                  LET'S GO! <Rocket size={26} />
                </motion.button>
              </motion.div>
            </motion.div>
          )}

          {stage === "challenge" && (
            <motion.div
              key={`challenge-${round}`}
              data-testid="wcyd-challenge"
              className="text-center max-w-4xl w-full"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
            >
              <p className="font-mono text-sm sm:text-base uppercase tracking-[0.25em] text-[#E8564A] mb-4">{ch.intro}</p>
              <h2 data-testid="wcyd-question" className="font-display font-extrabold tracking-tight text-2xl sm:text-4xl lg:text-5xl text-walnut mb-10 max-w-3xl mx-auto leading-tight">
                {ch.question}
              </h2>
              <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-3xl mx-auto">
                {cards.map((c, i) => (
                  <motion.button
                    key={c.id}
                    data-testid={`wcyd-card-${c.id}`}
                    onClick={() => pick(c)}
                    className="group bg-white/85 border-2 border-sand rounded-3xl p-3 sm:p-5 shadow-lg cursor-pointer"
                    initial={{ y: 60, opacity: 0 }}
                    animate={
                      shakeId && shakeId.startsWith(c.id)
                        ? { x: [0, -12, 12, -9, 9, 0], y: 0, opacity: 1 }
                        : { y: 0, opacity: 1, x: 0 }
                    }
                    transition={{ delay: i * 0.12, type: "spring", stiffness: 140, damping: 12 }}
                    whileHover={{ scale: 1.06, rotate: -1.5 }}
                    whileTap={{ scale: 0.93 }}
                  >
                    <img src={c.img} alt={c.name} className="w-full h-32 sm:h-44 object-contain" />
                    <span className="block mt-2 font-display font-bold text-xs sm:text-sm text-walnut">{c.name}</span>
                  </motion.button>
                ))}
              </div>
              <AnimatePresence>
                {oops && (
                  <motion.p
                    data-testid="wcyd-try-again"
                    className="mt-8 inline-block bg-white/85 border-2 border-[#E8564A]/40 rounded-full px-6 py-3 font-display font-bold text-walnut"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    Not that one — try again, you can do it!
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {stage === "cheer" && (
            <motion.div
              key={`cheer-${round}`}
              data-testid="wcyd-cheer"
              className="text-center max-w-2xl relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -30 }}
            >
              {BURST.map((s, i) => (
                <motion.span
                  key={i}
                  className="absolute left-1/2 top-24 text-2xl pointer-events-none"
                  style={{ color: s.c }}
                  initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                  animate={{ x: s.x, y: s.y, scale: [0, 1.4, 1], opacity: [1, 1, 0] }}
                  transition={{ duration: 1.4, delay: 0.1 + i * 0.04, ease: "easeOut" }}
                >
                  ✦
                </motion.span>
              ))}
              <motion.img
                src={IMG[ch.cheerImg]}
                alt={ch.cheer}
                data-testid="wcyd-cheer-image"
                className="w-44 sm:w-60 mx-auto max-h-72 object-contain drop-shadow-[0_18px_24px_rgba(49,67,92,0.35)]"
                initial={{ scale: 0, rotate: -12 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 130, damping: 9 }}
              />
              <motion.h2
                className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-5xl text-walnut"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 150, damping: 10, delay: 0.15 }}
              >
                {ch.cheer}
              </motion.h2>
              <motion.p
                className="mt-3 font-display font-bold text-lg sm:text-2xl text-walnutsoft"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                Yes you can — well done!
              </motion.p>
              <motion.div
                className="mt-8"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
              >
                <button
                  data-testid="wcyd-next-button"
                  onClick={next}
                  className="rounded-full text-cream font-display font-extrabold text-lg sm:text-xl px-10 py-4 inline-flex items-center gap-2 hover:brightness-95 transition shadow-[0_16px_32px_-12px_rgba(232,86,74,0.6)]"
                  style={{ background: "#E8564A" }}
                >
                  {round < CHALLENGES.length - 1 ? (
                    <>What else? <Rocket size={20} /></>
                  ) : (
                    <>Get my gift <Star size={20} /></>
                  )}
                </button>
              </motion.div>
            </motion.div>
          )}

          {stage === "complete" && (
            <motion.div
              key="complete"
              data-testid="wcyd-complete"
              className="text-center max-w-2xl relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                {FIREWORKS.map((f, i) => (
                  <FireworkBurst key={i} {...f} />
                ))}
                <Streamer color="#E8564A" style={{ left: "6%" }} delay={0} />
                <Streamer color="#4FA3E3" style={{ left: "12%" }} delay={0.7} />
                <Streamer color="#71BE6B" style={{ right: "9%" }} delay={0.4} />
                <Streamer color="#F5A623" style={{ right: "4%" }} delay={1.1} />
                <Streamer color="#F5B8A6" style={{ left: "48%" }} delay={0.9} />
              </div>
              <motion.h2
                className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-walnut"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 150, damping: 10 }}
              >
                YOU'RE A WINNER!
              </motion.h2>
              <motion.img
                src={IMG.football}
                alt="Bailey celebrating everything he can do"
                data-testid="wcyd-winner-image"
                className="my-6 w-40 sm:w-52 mx-auto drop-shadow-[0_20px_30px_rgba(49,67,92,0.35)]"
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 120, damping: 8, delay: 0.35 }}
              />
              <motion.p
                className="font-display font-bold text-lg sm:text-2xl text-walnutsoft"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                Football, getting dressed, tidying up and painting — Bailey can do it all, and so can YOU!
              </motion.p>
              <motion.div
                className="mt-10 flex flex-wrap justify-center gap-4"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
              >
                <a
                  href="/downloads/what-can-you-do-gift.pdf"
                  download="Bailey-What-Can-You-Do-GIFT.pdf"
                  data-testid="wcyd-download-gift"
                  className="rounded-full text-cream font-display font-bold text-base px-8 py-4 inline-flex items-center gap-2 hover:brightness-95 transition shadow-[0_16px_32px_-12px_rgba(232,86,74,0.6)]"
                  style={{ background: "#E8564A" }}
                >
                  <Download size={18} /> Download my What Can You Do? gift
                </a>
                <Link
                  to="/books/bailey-what-can-you-do"
                  data-testid="wcyd-get-book"
                  className="rounded-full bg-terracotta text-cream font-display font-bold text-base px-8 py-4 inline-flex items-center gap-2 hover:bg-terracottadark transition-colors shadow-[0_16px_32px_-12px_rgba(79,163,227,0.7)]"
                >
                  <BookOpen size={18} /> Get the Bailey, What Can You Do? book
                </Link>
                <button
                  data-testid="wcyd-play-again"
                  onClick={start}
                  className="rounded-full border-2 border-walnut text-walnut font-display font-bold text-base px-8 py-4 inline-flex items-center gap-2 hover:bg-walnut hover:text-cream transition-colors"
                >
                  <RotateCcw size={18} /> Play again
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <div className="relative z-0 h-10 bg-sagecream border-t-4 border-dashed border-sand" />
    </div>
  );
}
